// The flow diagram shared by the Energy tab's power flow and the Vent tab's airflow: four 70 px nodes in the
// corners of a 330×230 box, linked to a pill in the centre, with small chevrons moving along each active link
// toward the receiver. The box scales down (never up) to the card width.
import * as React from 'react';

import { useElementWidth } from '../../util';
import { ALERT, CARD_BG, HAIRLINE, INK_SOFT, SECONDARY } from './tokens';

export const FLOW_W = 330;
export const FLOW_H = 230;
export const FLOW_HUB = { x: 165, y: 115 };
const LINK_LEN = 141; // node centre -> hub

export type FlowCorner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

// Node centre, the link's angle toward the hub, and the node box's top-left corner.
export const FLOW_NODES: Record<FlowCorner, { x: number; y: number; angle: number; left: number; top: number }> = {
  topLeft: { x: 45, y: 40, angle: 32, left: 10, top: 5 },
  topRight: { x: 285, y: 40, angle: 148, left: 250, top: 5 },
  bottomLeft: { x: 45, y: 190, angle: -32, left: 10, top: 155 },
  bottomRight: { x: 285, y: 190, angle: -148, left: 250, top: 155 },
};

export const FLOW_CSS = `
.ef-mv,.ef-mvr{animation-name:ef-mv;animation-timing-function:linear;animation-iteration-count:infinite}
.ef-mvr{animation-name:ef-mvr}
@keyframes ef-mv{0%{transform:translateX(38px);opacity:0}20%{opacity:1}80%{opacity:1}100%{transform:translateX(92px);opacity:0}}
@keyframes ef-mvr{0%{transform:translateX(92px);opacity:0}20%{opacity:1}80%{opacity:1}100%{transform:translateX(38px);opacity:0}}
@media (prefers-reduced-motion: reduce){.ef-mv,.ef-mvr{animation:none;transform:translateX(65px)}}
`;

export const clampDuration = (s: number) => Math.max(1.6, Math.min(3.6, s));

// One link from a corner node to the hub; `inflow` = the flow moves toward the hub. Nothing when inactive.
export function FlowLink(props: {
  corner: FlowCorner;
  active: boolean;
  inflow: boolean;
  color: string;
  durationS: number;
}): React.ReactElement | null {
  const { x, y, angle } = FLOW_NODES[props.corner];
  if (!props.active) {
    return null;
  }
  const dur = props.durationS;
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`}>
      <path d={`M0 0H${LINK_LEN}`} stroke={props.color} strokeOpacity={0.35} strokeWidth={2} />
      {[0, 1, 2].map((i) => (
        <g
          key={i}
          className={props.inflow ? 'ef-mv' : 'ef-mvr'}
          style={{ animationDuration: `${dur.toFixed(2)}s`, animationDelay: `${(-dur * i) / 3}s` }}
        >
          <path
            d={props.inflow ? 'M-3 -4L2 0L-3 4' : 'M3 -4L-2 0L3 4'}
            stroke={props.color}
            strokeWidth={2.2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ))}
    </g>
  );
}

// Style of a 70 px corner node.
export const flowNodeStyle = (corner: FlowCorner, extra: React.CSSProperties): React.CSSProperties => ({
  position: 'absolute',
  left: FLOW_NODES[corner].left,
  top: FLOW_NODES[corner].top,
  width: 70,
  height: 70,
  boxSizing: 'border-box',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  ...extra,
});

// The 124×50 pill over the hub.
export function FlowHub(props: { tint: string; title: string; sub?: string }): React.ReactElement {
  return (
    <div
      style={{
        position: 'absolute',
        left: FLOW_HUB.x - 62,
        top: FLOW_HUB.y - 25,
        width: 124,
        height: 50,
        boxSizing: 'border-box',
        borderRadius: 14,
        background: props.tint,
        boxShadow: `0 0 0 3px ${CARD_BG}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{props.title}</span>
      {props.sub && (
        <span className="num" style={{ fontSize: 11, color: INK_SOFT, lineHeight: 1.2 }}>
          {props.sub}
        </span>
      )}
    </div>
  );
}

// The scaled box: grey base links (and an optional dashed divider), the active `links`, the nodes as
// children. Greyed out when `stale`; `note` is a small label centred on top ("stale · 4 min ago").
export function FlowBox(props: {
  links: React.ReactNode;
  divider?: boolean;
  stale?: boolean;
  note?: { text: string; alert: boolean };
  children: React.ReactNode;
}): React.ReactElement {
  const [boxRef, width] = useElementWidth<HTMLDivElement>();
  const scale = width > 0 ? Math.min(1, width / FLOW_W) : 1;
  return (
    <div ref={boxRef} style={{ position: 'relative', height: FLOW_H * scale }}>
      <style>{FLOW_CSS}</style>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: Math.max(0, (width - FLOW_W * scale) / 2),
          width: FLOW_W,
          height: FLOW_H,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'top left',
          filter: props.stale ? 'grayscale(1)' : undefined,
          opacity: props.stale ? 0.55 : 1,
        }}
      >
        <svg
          width={FLOW_W}
          height={FLOW_H}
          viewBox={`0 0 ${FLOW_W} ${FLOW_H}`}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0 }}
        >
          {props.divider && <path d="M165 4V226" stroke={HAIRLINE} strokeWidth={1} strokeDasharray="3 4" />}
          <path
            d="M45 40L165 115M285 40L165 115M45 190L165 115M285 190L165 115"
            stroke={HAIRLINE}
            strokeWidth={4}
            strokeLinecap="round"
            fill="none"
          />
          {props.links}
        </svg>
        {props.children}
      </div>
      {props.note && (
        <span
          className="num"
          style={{
            position: 'absolute',
            left: '50%',
            top: 2,
            transform: 'translateX(-50%)',
            fontSize: 10,
            fontWeight: 600,
            color: props.note.alert ? ALERT.fg : SECONDARY,
            background: props.note.alert ? ALERT.bg : HAIRLINE,
            borderRadius: 4,
            padding: '1px 6px',
            whiteSpace: 'nowrap',
          }}
        >
          {props.note.text}
        </span>
      )}
    </div>
  );
}
